/**
 * Password Validation Utilities
 *
 * This module contains utility functions and types for validating passwords.
 * It's placed in the general library to avoid circular dependencies between
 * atoms and hooks.
 */

export interface PasswordValidation {
  lengthValid: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  passwordsMatch: boolean;
}

/**
 * Validates a password against common requirements
 * @param password - The password to validate
 * @param confirmPassword - The confirm password to match against (optional)
 * @returns PasswordValidation object with validation results
 */
export const validatePassword = (
  password: string,
  confirmPassword?: string
): PasswordValidation => {
  const lengthValid = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_{|}~]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const passwordsMatch =
    confirmPassword !== undefined ? password === confirmPassword : true;

  return {
    lengthValid,
    hasNumber,
    hasSpecialChar,
    hasUpperCase,
    hasLowerCase,
    passwordsMatch,
  };
};

/**
 * Checks if a password meets all validation requirements
 * @param password - The password to validate
 * @param confirmPassword - The confirm password to match against (optional)
 * @returns boolean indicating if password is valid
 */
export const isPasswordValid = (
  password: string,
  confirmPassword?: string
): boolean => {
  const validation = validatePassword(password, confirmPassword);
  return (
    validation.lengthValid &&
    validation.hasNumber &&
    // FLEXO-6112 (#8): re-enabled so registration actually enforces the special
    // character its on-screen checklist advertises. Previously it was disabled,
    // so accounts could be created without one — but the re-auth dialog required
    // it, permanently locking those users out of delete/credential changes.
    validation.hasSpecialChar &&
    validation.hasUpperCase &&
    validation.hasLowerCase &&
    validation.passwordsMatch
  );
};

/**
 * Checks if passwords match
 * @param password - The password
 * @param confirmPassword - The confirm password
 * @returns boolean indicating if passwords match
 */
export const doPasswordsMatch = (
  password: string,
  confirmPassword: string
): boolean => {
  return password === confirmPassword;
};
